import { Button } from '@blueprintjs/core';
import { showSuccessToast } from 'Components/Generic/BOSToasterUtil';
import ButtonFooter from 'Components/Generic/Buttons/ButtonFooter';
import CancelButton from 'Components/Generic/Buttons/CancelButton';
import StatusToggleButton from 'Components/Generic/Buttons/StatusToggleButton';
import DetailPanel from 'Components/Generic/DetailPanel';
import EditableDetailPanel from 'Components/Generic/DetailPanel/EditableDetailPanel';
import NotesListPanel from 'Components/Generic/Notes/NotesListPanel';
import { useAlertContext } from 'Data/context/Alerts';
import { OBJECT_STATUS, user_roles } from 'Data/Enum';
import ServiceContractData from 'Data/ServiceContractData';
import ServiceContractDetailsData from 'Data/ServiceContractDetailsData';
import ServiceContractDocumentData from 'Data/ServiceContractDocumentData';
import SiteDrawerData from 'Data/SiteDrawerData';
import DataManager from 'Lib/DataManager';
import { useStateWithRef } from 'Lib/hooks/useStateWithRef';
import TransactionList from 'Pages/Transactions/TransactionList';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { isUserAuthorized } from 'Util';
import ServiceContractDetailsInformation from './ServiceContractDetailsInformation';
import ServiceContractDocumentsPanel from './ServiceContractDocumentsPanel';
import ServiceContractInformationPanel from './ServiceContractInformationPanel';
import styles from './ServiceContracts.module.css';

let DM = new DataManager();

const ServiceContractView = ({
  serviceContractId,
  params,
  editable = false,
  compact = false,
  isNew = false,
  ...props
}) => {
  const navigate = useNavigate();

  const [serviceContract, setServiceContract, serviceContractRef] =
    useStateWithRef({});
  const [addNewResourceBoxOpen, setAddNewResourceBoxOpen] = useState(false);

  const { openConfirmationDialog, showDeletedToast } = useAlertContext();

  useEffect(() => {
    DM.add(
      ServiceContractData.registerListener(
        'BLANK_RECORD_LOADED',
        onServiceContractLoaded,
      ),
      ServiceContractData,
    );
    DM.add(
      ServiceContractData.registerListener(
        'RECORD_LOADED',
        onServiceContractLoaded,
      ),
      ServiceContractData,
    );
    DM.add(
      ServiceContractData.registerListener(
        'RECORD_CREATED',
        onServiceContractCreated,
      ),
      ServiceContractData,
    );
    DM.add(
      ServiceContractData.registerListener(
        'RECORD_ACTIVATED',
        onServiceContractActivated,
      ),
      ServiceContractData,
    );
    DM.add(
      ServiceContractData.registerListener(
        'RECORD_DEACTIVATED',
        onServiceContractDeactivated,
      ),
      ServiceContractData,
    );
    DM.add(
      ServiceContractDetailsData.registerListener(
        'RECORD_CREATED',
        onServiceContractDetailsCreated,
      ),
      ServiceContractDetailsData,
    );
    DM.add(
      ServiceContractDetailsData.registerListener(
        'RECORDS_DELETED',
        onServiceContractDetailsRemoved,
      ),
      ServiceContractDetailsData,
    );
    DM.add(
      ServiceContractDocumentData.registerListener(
        'RECORDS_DELETED',
        onDocumentDeleted,
      ),
      ServiceContractDocumentData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  useEffect(() => {
    if (!serviceContractId && !params?.id) {
      ServiceContractData.getBlank();
    }
  }, []);

  useEffect(() => {
    if (serviceContractId && serviceContractId > 0) {
      getServiceContract(serviceContractId);
    }
  }, [serviceContractId]);

  useEffect(() => {
    if (params?.id && !serviceContractId) {
      getServiceContract(params.id);
    }
  }, [params]);

  const refreshServiceContract = useCallback(() => {
    serviceContract?.id && getServiceContract(serviceContract.id);
  }, [serviceContract]);
  const getServiceContract = (id) => {
    ServiceContractData.getServiceContractAndAssociations(id);
  };

  const onServiceContractLoaded = (payload) => {
    if (payload) {
      setServiceContract(payload);
    }
  };

  const onServiceContractCreated = (payload) => {
    if (payload) {
      setAddNewResourceBoxOpen(true);
    }
  };

  let statusCancelButton =
    editable &&
    isUserAuthorized({
      roles: [user_roles.SC_ADMIN],
    }) &&
    (serviceContract?.id ? (
      <StatusToggleButton
        currentStatus={serviceContract.status}
        onStatusToggleClicked={() => {
          statusToggleClicked();
        }}
      />
    ) : (
      <CancelButton onCancel={SiteDrawerData.close} />
    ));

  const toggleServiceContractStatus = () => {
    serviceContractRef.current?.status === OBJECT_STATUS.ACTIVE.key
      ? ServiceContractData.deactivateRecord(serviceContractRef.current?.id)
      : ServiceContractData.activateRecord(serviceContractRef.current?.id);
  };

  const statusToggleClicked = () => {
    let currentStatusActive =
      serviceContractRef.current?.status === OBJECT_STATUS.ACTIVE.key;
    openConfirmationDialog({
      onConfirm: toggleServiceContractStatus,
      dialogTitle: currentStatusActive
        ? 'Deactivate Service Contract?'
        : 'Reactivate Service Contract',
      icon: currentStatusActive ? 'disable' : undefined,
      intent: currentStatusActive ? 'danger' : 'primary',
    });
  };

  const onServiceContractActivated = (payload) => {
    showSuccessToast('Service Contract Reactivated');
    getServiceContract(payload.id);
  };

  const onServiceContractDeactivated = (payload) => {
    showSuccessToast('Service Contract Deactivated');
    getServiceContract(payload.id);
  };

  const onServiceContractDetailsCreated = (payload) => {
    setAddNewResourceBoxOpen(false);

    showSuccessToast('Core/Asset Linked to Service Contract');
    if (isNew) {
      navigate(`./${serviceContractRef.current?.id}`);
      SiteDrawerData.close();
    } else {
      getServiceContract(serviceContractRef.current?.id);
    }
  };

  const onServiceContractNotesUpdated = () => {
    getServiceContract(serviceContractRef.current?.id);
  };

  const onServiceContractDetailsRemoved = () => {
    showSuccessToast('Associated Core/Asset Unlinked from Service Contract');
    getServiceContract(serviceContractRef.current?.id);
  };

  const onDocumentDeleted = () => {
    showDeletedToast('Document');
  };

  let addNewResourcePanel = (isNew || addNewResourceBoxOpen) &&
    serviceContract?.id && (
      <ServiceContractDetailsInformation
        editable
        hideToggle
        onCancel={() => setAddNewResourceBoxOpen(false)}
        parentEditState={true}
        serviceContractId={serviceContract?.id}
      />
    );
  const onClick = () => {
    setAddNewResourceBoxOpen(true);
  };

  let linkResourceButton = isUserAuthorized({
    roles: [user_roles.SC_ADMIN],
  }) &&
    serviceContract?.id &&
    editable &&
    !addNewResourceBoxOpen && (
      <Button className={styles.link_resource_button} onClick={onClick}>
        {`Link ${
          serviceContract.service_contract_details?.length > 0 ? 'Another ' : ''
        }Core Facility/Asset`}
      </Button>
    );

  return (
    serviceContract && (
      <div
        style={{
          display: 'flex',
          gap: 10,
          flexDirection: 'column',
        }}>
        <div className={styles.panel_flex_box}>
          <div
            className={compact ? styles.full_width_panel : styles.half_panel}>
            <ServiceContractInformationPanel
              title='Details'
              editable={editable || isNew}
              collapsible={false}
              parentEditState={isNew}
              hideToggle={isNew}
            />
          </div>
          {serviceContract.id && (
            <div
              className={compact ? styles.full_width_panel : styles.half_panel}>
              {
                <EditableDetailPanel
                  title={'Covered Facilities/Assets'}
                  editable
                  collapsible={!isNew}
                  hideToggle={isNew}
                  renderChild={(inEditState) => {
                    return (
                      <div className={styles.panel_content_box_vertical}>
                        {serviceContract?.id &&
                          serviceContract.service_contract_details?.map(
                            (scDetails, index) => {
                              return (
                                <ServiceContractDetailsInformation
                                  editable={inEditState}
                                  key={index}
                                  serviceContractDetailsObject={scDetails}
                                  serviceContractId={serviceContract?.id}
                                />
                              );
                            },
                          )}
                        {addNewResourcePanel}
                      </div>
                    );
                  }}
                  renderFooter={() => {
                    return <ButtonFooter> {linkResourceButton} </ButtonFooter>;
                  }}
                />
              }
            </div>
          )}
          {serviceContract.id && !isNew && (
            <NotesListPanel
              notesIdList={
                serviceContract?.service_contract_notes?.map(
                  (note) => note.id,
                ) || []
              }
              notesTableName='service_contract_notes'
              dataIdentifier='SC_NOTES'
              canAddNotes={!!serviceContract.id}
              editable={editable}
              hideToggle
              parentEditState={editable || !!serviceContract.id}
              newNoteFieldValues={{ service_contract_id: serviceContract.id }}
              onNotesUpdated={onServiceContractNotesUpdated}
              collapsible
            />
          )}
          {serviceContract.id && !isNew && (
            <ServiceContractDocumentsPanel
              onDocumentsUpdated={refreshServiceContract}
              serviceContract={serviceContract}
              editable={editable}
              collapsible
            />
          )}
          {serviceContract.transactions?.length > 0 && (
            <div className={styles.full_width_panel}>
              <DetailPanel title={'Transactions'} collapsible>
                <div
                  style={{
                    paddingLeft: compact ? 0 : 20,
                    paddingRight: compact ? 0 : 20,
                  }}>
                  <TransactionList
                    transactions={serviceContract.transactions}
                  />
                </div>
              </DetailPanel>
            </div>
          )}
        </div>

        {statusCancelButton}
      </div>
    )
  );
};

ServiceContractView.propTypes = {
  serviceContractId: PropTypes.number,
  params: PropTypes.shape({ id: PropTypes.string }),
  editable: PropTypes.bool,
  compact: PropTypes.bool,
  isNew: PropTypes.bool,
};

export default ServiceContractView;
